// src/lib/spread-sign.js
//
// Single job (#1132 D1.2): decide whether a spread leg's stored line needs the user's
// explicit sign before it may be saved. Pure; shared by the confirm screen
// (save-blockers.js, PR 1) and the import-confirm gate (PR 2), so both apply one rule.
//
// Reads: a leg's resolution_warnings, a canonical market id, a numeric stored line and
// the user's lineSign answer. Writes: nothing. Does NOT compute the stored line (that
// is signed-line.js) or resolve markets.

import { normalizeRawLine } from './signed-line';
//
// The #1132 defect is a DROPPED MINUS: a favourite saved as "KC 3.5" grades as the
// underdog. A stored line at or below zero cannot carry it (a typed negative, the
// pick's printed minus, or a pick'em 0), so only a POSITIVE line needs an answer.

export const SPREAD_SIGN_CODES = ['UNSIGNED_SPREAD_LINE', 'SPREAD_SIGN_CONFLICT'];

// Exactly the calcStatus branches that read the line's sign (betting.js spread / pl).
// #1655 B1 (Ultron P1-2): plus nba:spread AHEAD of its grader (D1). It is stored now and
// graded later with no reprocessing, so a dropped minus must be caught at import, not
// when D1 lands.
export const SIGNED_LINE_MARKETS = new Set(['spread', 'nfl:spread', 'pl', 'nba:spread']);

export function hasSpreadSignFlag(leg) {
  const rw = leg && leg.resolution_warnings;
  return Array.isArray(rw) && SPREAD_SIGN_CODES.some((c) => rw.includes(c));
}

// A leg the parse did not read as a signed-line market, now on one, with a positive
// line: its sign was never established (Ultron P2-4, e.g. an unresolved
// "Alternate Puck Line" re-labelled "Puck Line").
export function isMarketEditFlag({ originalCanonical, nowSigned, line }) {
  return !SIGNED_LINE_MARKETS.has(originalCanonical) && !!nowSigned && line > 0;
}

// 'missing_line' | 'mismatch' | 'ok' | 'unconfirmed'. First rule that applies wins.
// Any lineSign other than the strings '+' and '-' counts as no answer.
export function spreadSignState(line, lineSign) {
  if (typeof line !== 'number' || !Number.isFinite(line)) return 'missing_line';
  if ((lineSign === '+' && line < 0) || (lineSign === '-' && line > 0)) return 'mismatch';
  if (line <= 0) return 'ok';
  if (lineSign === '+') return 'ok';
  return 'unconfirmed';
}

// ── Confirm-screen edit rules (#1132 D5.3): the last explicit sign wins ──────────────
// A number input cannot express "+", so the sign is a separate answer (leg.lineSign).

// The line after choosing `sign`: sign x |line| when the line is finite and non-zero.
// The original value is returned untouched when its sign already agrees, so a "+" on a
// positive line never rewrites it (answering "+" is not a correction).
export function applyLineSign(line, sign) {
  if (sign !== '+' && sign !== '-') return line;
  // The stored-line reading (normalizeRawLine), not numericLineOrNull: a dirty unsigned
  // string such as "1.5(match)" is 1.5 to the server, so "-" must be able to rewrite it
  // (pre-Codex FC Finding 1); an odds-sized value is not a line and stays untouched.
  const n = normalizeRawLine(line).line;
  if (n == null || n === 0) return line;
  const signed = sign === '-' ? -Math.abs(n) : Math.abs(n);
  return signed === n ? line : signed;
}

// { line, lineSign } after the user types `value` into the line box. A negative line
// sets '-'; a positive line while a sign is chosen takes that sign; 0 or a cleared box
// leaves the answer alone.
export function editSpreadLine(prevLineSign, value) {
  if (typeof value === 'number' && value < 0) return { line: value, lineSign: '-' };
  if (typeof value === 'number' && value > 0 && (prevLineSign === '+' || prevLineSign === '-')) {
    return { line: applyLineSign(value, prevLineSign), lineSign: prevLineSign };
  }
  return { line: value, lineSign: prevLineSign };
}
